import { ANCHOR_LABELS, ANCHOR_TYPE, START_OR_END, RESOLVE_TO, VISIT_STATUS } from '../constants'
import { formatFixedDateDisplay } from './fixedDateDisplay'
import { hidesOffset, offsetDays } from '../hooks/anchorConstraints'

const START_END_TYPES = [
  ANCHOR_TYPE.arm,
  ANCHOR_TYPE.phase,
  ANCHOR_TYPE.visitCycle,
  ANCHOR_TYPE.track,
  ANCHOR_TYPE.chapter,
]

const VISIT_STATUS_SHORT = {
  [VISIT_STATUS.targetDate]: 'target date',
  [VISIT_STATUS.appointmentScheduledDate]: 'scheduled date',
  [VISIT_STATUS.completedDate]: 'completed',
  [VISIT_STATUS.missedDate]: 'missed',
}

const formatOffset = anchor => {
  if (hidesOffset(anchor?.type)) return ''
  const offset = anchor?.offset || {}
  if (offset.direction && (offset.amount || offset.amount === 0)) {
    const amount = Number(offset.amount || 0)
    if (!amount) return ''
    const unit = offset.unit || 'days'
    const unitLabel = amount === 1 ? unit.replace(/s$/, '') : unit
    return `${amount} ${unitLabel} ${offset.direction}`
  }
  const days = offsetDays(anchor)
  if (!days) return ''
  const abs = Math.abs(days)
  const unit = abs === 1 ? 'day' : 'days'
  return days < 0 ? `${abs} ${unit} before` : `${abs} ${unit} after`
}

const startEndLabel = anchor => {
  if (!START_END_TYPES.includes(anchor?.type)) return ''
  return anchor?.startOrEnd === START_OR_END.end ? 'End' : 'Start'
}

const shortUnit = unit => {
  if (unit === 'hours') return 'h'
  if (unit === 'minutes') return 'm'
  if (unit === 'weeks') return 'w'
  return 'd'
}

export const shortOffset = anchor => {
  if (hidesOffset(anchor?.type)) return ''
  const amount = Number(anchor?.offset?.amount ?? 0)
  if (!amount) return ''
  const unit = shortUnit(anchor?.offset?.unit || 'days')
  const sign = anchor?.offset?.direction === 'before' ? '-' : '+'
  return `${sign}${amount} ${unit}`
}

export const summarizeAnchor = (anchor, entityName) => {
  const typeLabel = ANCHOR_LABELS[anchor?.type] || 'anchor'
  const name = entityName || ''
  if (anchor?.type === ANCHOR_TYPE.fixedCalendarDate) {
    const label = formatFixedDateDisplay(anchor.fixedDate)
    return label ? `on ${label}` : 'on exact date'
  }
  const offset = formatOffset(anchor)
  const startEnd = startEndLabel(anchor)
  const entityBit = [name, startEnd].filter(Boolean).join(' ')
  const subject = entityBit || typeLabel
  if (!offset) return subject
  return `${offset} ${subject}`
}

export const summarizeAnchorShort = (anchor, entityName) => {
  if (!anchor) return ''
  if (anchor.type === ANCHOR_TYPE.fixedCalendarDate) {
    return summarizeAnchor(anchor)
  }
  const name = entityName || ANCHOR_LABELS[anchor.type] || 'anchor'
  const offset = shortOffset(anchor)
  const startEnd = startEndLabel(anchor).toLowerCase()
  if (anchor.type === ANCHOR_TYPE.visit) {
    const status = VISIT_STATUS_SHORT[anchor.visitStatus] || 'visit'
    return name ? `${name} — ${status}` : status
  }
  if (anchor.type === ANCHOR_TYPE.participantStartDate) {
    const full = formatOffset(anchor)
    return full ? `${full} start` : 'at participant start'
  }
  const bits = [name, startEnd, offset].filter(Boolean)
  return bits.join(' ')
}

export const summarizeCombinedAnchors = (rule, nameForAnchor) => {
  const parts = (rule.anchors || []).map((anchor, index) =>
    summarizeAnchor(anchor, nameForAnchor?.(anchor, index)),
  )
  const joiner = rule.resolveTo === RESOLVE_TO.earliestOf ? 'earlier of' : 'later of'
  if (parts.length < 2) return parts[0] || ''
  return `the ${joiner} ${parts.join(' or ')}`
}

export const summarizeCombinedShort = (rule, nameForAnchor) => {
  const parts = (rule.anchors || []).map((anchor, index) =>
    summarizeAnchorShort(anchor, nameForAnchor?.(anchor, index)),
  )
  const joiner = rule.resolveTo === RESOLVE_TO.earliestOf ? 'Earliest' : 'Latest'
  if (parts.length < 2) return parts[0] || ''
  return `${joiner} of ${parts.join(' or ')}`
}
